/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import AppContext from "AppContext";
import { useDispatch, useSelector } from "react-redux";
import Page from "components/common_components/Page/Page";
import { notifyError, notifySuccess, notifyWarning } from "redux/actions/Notifications";
import { Button as ButtonMui, LinearProgress } from "@material-ui/core";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/PhoneIphone";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { useHistory } from "react-router-dom";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CloudDoneIcon from "@material-ui/icons/CloudDone";
import { hasElements } from "utils/arrays";
import { Tenant } from "utils/tenants";
import Icon from "components/common_components/Icon";
import LoadingPage from "components/common_components/LoadingPage";
import Button from "components/common_components/Button";
import { Form, InputField, SelectField } from "components/common_components/Formik";
import Status from "components/common_components/Status";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0px 30px",
        fontSize: "large",
        marginBottom: 20,
        marginTop: 30,
    },
    title: {
        fontSize: "large",
    },
    linearProgress: {
        width: 50,
        marginLeft: 5,
    },
}));

const DEFAULT_SMS_BODY =
    "Hola, nos estamos poniendo en contacto con usted desde la Oficina Legal de Diller V. Paz.\n\n" +
    "Lo estamos contactando porque nuestro sistema muestra que no ha realizado un pago en más de 90 días. Por favor, póngase en contacto con la oficina para realizar un pago al 281-401-9609.\n\n" +
    `${Tenant.getName()}.`;

const defaultContactTypes = {
    P_EML: "-1",
    P_SMS: "-2",
};

function PendingPayments(props) {
    const classes = useStyles();
    const [pendingPayments, setPendingPayments] = useState(undefined);
    const [emailSentIds, setEmailSentIds] = useState([]);
    const [emailSendingIds, setEmailSendingIds] = useState([]);
    const [smsSentIds, setSmsSentIds] = useState([]);
    const [smsSendingIds, setSmsSendingIds] = useState([]);
    const dispatch = useDispatch();
    const history = useHistory();
    const { user } = useSelector((state) => state.Login);
    const [isLoading, setIsLoading] = useState(false);
    const [resources, setResources] = useState({ modules: [], types: [] });
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);

    const {
        api: {
            Modules,
            General: { PendingPayments },
            Settings: { Modules: SModules, Types },
        },
    } = useContext(AppContext);

    function getModules() {
        setIsLoadingFilters(true);
        SModules.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    modules: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    modules: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function getTypes() {
        setIsLoadingFilters(true);
        Types.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    types: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    types: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function registerProgressInfo(module, fileCaseId, contactTypeId) {
        return Modules(module).ProgressInfo.addAsync({
            fileCaseId,
            date: new Date().getTime(),
            contactTypeId,
            isImportant: false,
            comments: DEFAULT_SMS_BODY,
            userId: user.id,
        });
    }

    async function registerProgressInfoSingle(module, fileCaseId, contactTypeId) {
        try {
            const response = await registerProgressInfo(module, fileCaseId, contactTypeId);
            if (response && response.status === 200) {
                dispatch(notifySuccess("Progress Information saved successfully!"));
            }
        } catch (error) {
            dispatch(notifyError("Unable to register progress information!"));
            console.error("Unable to register progress information!", error);
        }
    }

    async function registerProgressInfoBulk(items, contactTypeId) {
        try {
            const pp_promises = items.map(({ fileCaseId, module }) => {
                return registerProgressInfo(module, fileCaseId, contactTypeId);
            });

            const pp_responses = await Promise.all(pp_promises);
            if (hasElements(pp_responses)) {
                dispatch(notifySuccess("Progress Information saved successfully!"));
            }
        } catch (error) {
            dispatch(notifyError("Unable to register progress information!"));
            console.error("Unable to register progress information!", error);
        }
    }

    const getPercentile = (sortedValues, percentile) => {
        if (!hasElements(sortedValues)) return 0;
        const index = Math.ceil((percentile / 100) * sortedValues.length) - 1;
        return sortedValues[Math.min(index, sortedValues.length - 1)]?.balance || 0;
    };

    const getPriority = (balance, balancePercentile50, balancePercentile80) => {
        if (balance <= balancePercentile50) return "low";
        if (balance <= balancePercentile80) return "medium";
        return "high";
    };

    const getPriorityStyle = (daysWithoutPaying, priority) => {
        if (daysWithoutPaying > 365 && priority === "high") {
            return { category: "error", label: "High", order: 1 };
        }
        if ((daysWithoutPaying >= 180 && daysWithoutPaying <= 365) || priority === "medium") {
            return { category: "warning", label: "Medium", order: 2 };
        }
        if (daysWithoutPaying < 180 && priority === "low") {
            return { category: "success", label: "Low", order: 3 };
        }
        return { category: "warning", label: "Medium", order: 2 };
    };

    async function getPendingPayments(filters = {}) {
        try {
            setIsLoading(true);
            const res = await PendingPayments.getAllAsync({ data: filters });
            if (hasElements(res)) {
                const sortedByBalance = res.sort((a, b) => a.balance - b.balance);
                const balancePercentile50 = getPercentile(sortedByBalance, 50);
                const balancePercentile80 = getPercentile(sortedByBalance, 80);
                const prioritizedPayments = res.map((item) => {
                    const priority = getPriority(item.balance, balancePercentile50, balancePercentile80);
                    return {
                        ...item,
                        priorityStyle: {
                            ...getPriorityStyle(item.daysWithoutPaying, priority),
                        },
                    };
                });
                setPendingPayments(prioritizedPayments.sort((a, b) => a.priorityStyle.order - b.priorityStyle.order));
            } else {
                setPendingPayments([]);
            }
        } catch (error) {
            dispatch(notifyError("Unable to fetch pending payments."));
            console.error("Unable to fetch pending payments!", error);
            setPendingPayments([]);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getModules();
        getTypes();

        return () => {
            setPendingPayments([]);
        };
    }, []);

    async function onSendEmailClickHandler(event, item) {
        event.preventDefault();
        event.stopPropagation();
        // Send email
        try {
            setEmailSendingIds((prev) => [...prev, item.id]);
            const res = await PendingPayments.sendEmailAsync({ payload: { clientId: item.clientId } });
            if (res) {
                setEmailSentIds((prev) => [...prev, item.id]);
                dispatch(notifySuccess("Email sent!"));

                // Register progress information
                await registerProgressInfoSingle(item.module, item.fileCaseId, defaultContactTypes.P_EML);
            } else {
                dispatch(notifyError("Email not sent. Ask an administrator for more information."));
            }
        } catch (error) {
            dispatch(notifyError("Email not sent. \n" + error));
        } finally {
            setEmailSendingIds((prev) => prev.filter((x) => x !== item.id));
        }
    }

    async function onSendSMSClickHandler(event, item) {
        event.preventDefault();
        event.stopPropagation();
        // Send SMS
        try {
            setSmsSendingIds((prev) => [...prev, item.id]);
            const res = await PendingPayments.sendSMSAsync({ payload: { clientId: item.clientId, body: DEFAULT_SMS_BODY } });
            if (res) {
                setSmsSentIds((prev) => [...prev, item.id]);
                dispatch(notifySuccess("SMS sent!"));

                // Register progress information
                await registerProgressInfoSingle(item.module, item.fileCaseId, defaultContactTypes.P_SMS);
            } else {
                dispatch(notifyError("SMS not sent. Ask an administrator for more information."));
            }
        } catch (error) {
            dispatch(notifyError("SMS not sent. \n" + error));
        } finally {
            setSmsSendingIds((prev) => prev.filter((x) => x !== item.id));
        }
    }

    const headCells = [
        {
            index: 0,
            id: "priorityStyle",
            label: "Priority",
            render: (_, { value }) => {
                return <Status {...value} />;
            },
        },
        {
            index: 1,
            id: "fileNo",
            disablePadding: false,
            label: "File No.",
            render: (_, { value, data }) => {
                return (
                    <a
                        href="#top"
                        onClick={() => {
                            const { module, fileCaseId } = data;
                            if (module && fileCaseId) {
                                history.push(`/${module}?fileCaseId=${fileCaseId}`);
                            }
                        }}
                    >
                        {value}
                    </a>
                );
            },
        },
        { index: 2, id: "type", disablePadding: false, label: "Type" },
        { index: 3, id: "clientFullName", disablePadding: false, label: "Client" },
        {
            index: 4,
            id: "clientEmail",
            disablePadding: false,
            label: "Email",
            render: (_, { value, data }) => {
                return (
                    <a
                        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${String(value).trim().toLowerCase()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {value}
                    </a>
                );
            },
        },
        { index: 5, id: "lastPaymentDateString", disablePadding: false, label: "Last Payment" },
        { index: 6, id: "daysWithoutPaying", disablePadding: false, label: "Days Count" },
        { index: 7, id: "balance", disablePadding: false, label: "Balance", type: "currency" },
        {
            index: 8,
            id: "actions",
            disablePadding: false,
            label: "Actions",
            render: (_, { value, data }) => {
                if (!data.clientEmail) {
                    return null;
                }
                if (emailSendingIds.includes(data.id) || smsSendingIds.includes(data.id)) {
                    return <LinearProgress className={classes.linearProgress} />;
                }
                if (emailSentIds.includes(data.id) || smsSentIds.includes(data.id)) {
                    return (
                        <ButtonMui variant="text" disabled size="small" color="primary" endIcon={<CloudDoneIcon />}>
                            Sent!
                        </ButtonMui>
                    );
                }
                return (
                    <>
                        <ButtonMui
                            variant="text"
                            size="small"
                            onClick={(e) => onSendEmailClickHandler(e, data)}
                            color="default"
                            startIcon={<EmailIcon color="primary" />}
                        >
                            Email
                        </ButtonMui>
                        <ButtonMui
                            variant="text"
                            size="small"
                            onClick={(e) => onSendSMSClickHandler(e, data)}
                            color="default"
                            startIcon={<PhoneIcon color="primary" />}
                        >
                            SMS
                        </ButtonMui>
                    </>
                );
            },
        },
    ];

    async function onSendEmailsBulkClickHandler(selectedItems, clearSelection) {
        // Send emails
        const promises = selectedItems.map((x) => PendingPayments.sendEmailAsync({ payload: { clientId: x.clientId, id: x.id } }));

        try {
            setEmailSendingIds(selectedItems.map((x) => x.id));
            const responses = await Promise.all(promises);

            if (hasElements(responses)) {
                const successfullResponses = responses.filter((x) => x.data === true);
                const newEmailSentIds = successfullResponses.map((x) => JSON.parse(x.config.data).id);
                setEmailSentIds((prev) => [...prev, ...newEmailSentIds]);
                if (successfullResponses.length === responses.length) {
                    dispatch(notifySuccess("Emails sent!"));
                } else {
                    dispatch(notifyWarning("Some emails weren't sent!"));
                }

                // Register progress information Bulk
                const items = selectedItems.filter((x) => newEmailSentIds.includes(x.id));
                await registerProgressInfoBulk(items, defaultContactTypes.P_EML);
            }
        } catch (error) {
            dispatch(notifyError("Emails not sent. \n" + error));
        } finally {
            setEmailSendingIds([]);
            clearSelection();
        }
    }

    async function onSendSMSBulkClickHandler(selectedItems, clearSelection) {
        // Send SMSs
        const promises = selectedItems.map((x) =>
            PendingPayments.sendSMSAsync({ payload: { clientId: x.clientId, body: DEFAULT_SMS_BODY, id: x.id } })
        );

        try {
            setSmsSendingIds(selectedItems.map((x) => x.id));
            const responses = await Promise.all(promises);

            if (hasElements(responses)) {
                const successfullResponses = responses.filter((x) => x.data === true);
                const newSmsSentIds = successfullResponses.map((x) => JSON.parse(x.config.data).id);
                setSmsSentIds((prev) => [...prev, ...newSmsSentIds]);
                if (successfullResponses.length === responses.length) {
                    dispatch(notifySuccess("SMSs sent!"));
                } else {
                    dispatch(notifyWarning("Some SMSs weren't sent!"));
                }

                // Register progress information Bulk
                const items = selectedItems.filter((x) => newSmsSentIds.includes(x.id));
                await registerProgressInfoBulk(items, defaultContactTypes.P_SMS);
            }
        } catch (error) {
            dispatch(notifyError("SMSs not sent. \n" + error));
        } finally {
            setSmsSendingIds([]);
            clearSelection();
        }
    }

    function renderBulkActions(selectedItemIds, clearSelection) {
        const selectedItems = pendingPayments
            .filter((x) => selectedItemIds.includes(x.id))
            .map(({ id, clientId, module, fileCaseId }) => ({ id, clientId, module, fileCaseId }));
        return (
            <>
                <Tooltip title="Send emails" onClick={() => onSendEmailsBulkClickHandler(selectedItems, clearSelection)}>
                    <IconButton aria-label="send-emails">
                        <EmailIcon color="primary" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Send SMS messages" onClick={() => onSendSMSBulkClickHandler(selectedItems, clearSelection)}>
                    <IconButton aria-label="send-emails">
                        <PhoneIcon color="primary" />
                    </IconButton>
                </Tooltip>
            </>
        );
    }

    function onSubmitHandler(values, { setSubmitting }) {
        setSubmitting(false);
        const { module, type, daysWithoutPayingRangeFrom, daysWithoutPayingRangeTo, balanceRangeFrom, balanceRangeTo } = values;
        const filterValues = {};
        if (module) filterValues.module = resources.modules.find((x) => x.id === module).dbName;
        if (type) filterValues.typeId = type;
        if (daysWithoutPayingRangeFrom) filterValues.daysWithoutPayingRangeFrom = daysWithoutPayingRangeFrom;
        if (daysWithoutPayingRangeTo) filterValues.daysWithoutPayingRangeTo = daysWithoutPayingRangeTo;
        if (balanceRangeFrom) filterValues.balanceRangeFrom = balanceRangeFrom;
        if (balanceRangeTo) filterValues.balanceRangeTo = balanceRangeTo;

        getPendingPayments(filterValues);
    }

    return (
        <Page className="pending-payments-page">
            <div className="filters-container">
                {isLoadingFilters ? (
                    <LoadingPage />
                ) : (
                    <Form
                        validateOnChange
                        initialValues={{
                            module: null,
                            type: null,
                            daysWithoutPayingRangeFrom: null,
                            daysWithoutPayingRangeTo: null,
                            balanceRangeFrom: null,
                            balanceRangeTo: null,
                        }}
                        validationSchema={undefined}
                        onSubmit={onSubmitHandler}
                        showButtons={false}
                    >
                        {(form) => (
                            <>
                                {" "}
                                <SelectField fieldName="module" label="Module" options={resources.modules} valueKey="id" labelKey="name" />
                                <SelectField
                                    fieldName="type"
                                    label="Type"
                                    options={
                                        form.values.module
                                            ? resources.types.filter((x) => x.moduleId === form.values.module)
                                            : resources.types
                                    }
                                    valueKey="id"
                                    labelKey="type"
                                />
                                <InputField type="number" fieldName="daysWithoutPayingRangeFrom" label="Days Count From" />
                                <InputField type="number" fieldName="daysWithoutPayingRangeTo" label="Days Count To" />
                                <InputField type="number" fieldName="balanceRangeFrom" label="Balance From" />
                                <InputField type="number" fieldName="balanceRangeTo" label="Balance To" />
                                <Button secondary small type="submit" loading={isLoading}>
                                    <Icon name="search" size="md" color="white" />
                                    <span>Search</span>
                                </Button>
                            </>
                        )}
                    </Form>
                )}
            </div>
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {emailSentIds && hasElements(pendingPayments) && (
                        <EnhancedTable
                            headCells={headCells}
                            data={pendingPayments}
                            keyColumnName="id"
                            defaultOrder={{ order: "asc", orderBy: "ordinal" }}
                            selectable={true}
                            deletable={false}
                            filterable={false}
                            pageable={false}
                            renderBulkActions={renderBulkActions}
                        />
                    )}
                </>
            )}
        </Page>
    );
}

export default PendingPayments;
